import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { AlertTriangle, Check, Copy, RotateCcw } from 'lucide-react';
import { ObjectiveTable, TaskTable, ObjectiveCompactList, TaskCompactList } from './ObjectiveComponents';
import Markdown from './Markdown';
import RunSteps from './RunSteps';

const SPECIAL_BLOCK_RE = /```(objectives-table|tasks-table|objectives-compact|tasks-compact)\n([\s\S]*?)\n```/g;

// Blocs spéciaux (tableaux d'objectifs ou de tâches) rendus en composants, le reste en Markdown.
const renderContent = (content) => {
  const parts = [];
  let last = 0;
  let match;
  SPECIAL_BLOCK_RE.lastIndex = 0;
  while ((match = SPECIAL_BLOCK_RE.exec(content)) !== null) {
    let data = null;
    try {
      data = JSON.parse(match[2]);
    } catch (error) {
      console.error("Erreur lors de l'analyse du bloc spécial:", error);
    }
    if (data) {
      parts.push({ text: content.slice(last, match.index) });
      parts.push({ type: match[1], data });
      last = match.index + match[0].length;
    }
  }
  parts.push({ text: content.slice(last) });
  return parts.map((part, index) => {
    if (part.type === 'objectives-table') return <ObjectiveTable key={index} objectives={part.data?.objectives || []} />;
    if (part.type === 'tasks-table') return <TaskTable key={index} tasks={part.data?.tasks || []} />;
    if (part.type === 'objectives-compact') return <ObjectiveCompactList key={index} objectives={part.data?.objectives || []} />;
    if (part.type === 'tasks-compact') return <TaskCompactList key={index} tasks={part.data?.tasks || []} />;
    return part.text.trim() ? <Markdown key={index}>{part.text}</Markdown> : null;
  });
};

const CopyButton = ({ text }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button type="button" onClick={copy} title="Copier la réponse"
      className="inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 text-[11px] text-slate-400 transition hover:bg-slate-100 hover:text-slate-600">
      {copied ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3" />}
      {copied ? 'Copié' : 'Copier'}
    </button>
  );
};

CopyButton.propTypes = { text: PropTypes.string.isRequired };

const ChatBubble = ({ message, live = false, onRetry = null }) => {
  const { content = '', role, isError, run, streaming } = message;

  if (role === 'user') {
    return (
      <div className="flex justify-end">
        <div className="max-w-[85%] whitespace-pre-wrap break-words rounded-2xl rounded-br-md bg-indigo-600 px-4 py-2.5 text-[0.92rem] leading-relaxed text-white shadow-sm">
          {content}
        </div>
      </div>
    );
  }

  if (role === 'system') {
    return (
      <div className="flex justify-center">
        <p className="rounded-full bg-slate-100 px-3 py-1 text-xs text-slate-600">{content}</p>
      </div>
    );
  }

  const writing = Boolean(streaming && content);
  return (
    // Pas d'avatar devant chaque réponse : le robot de l'en-tête incarne déjà Nexus (ou Muse).
    <div className="group">
      <div className="min-w-0">
        {run && <RunSteps run={run} live={live} writing={writing || !live} />}
        {isError ? (
          <div className="flex items-start gap-2 rounded-xl border border-rose-200 bg-rose-50 px-3 py-2 text-sm text-rose-700">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="min-w-0 flex-1">
              <Markdown className="text-rose-700">{content}</Markdown>
              {onRetry && (
                <button type="button" onClick={onRetry}
                  className="mt-1.5 inline-flex items-center gap-1 rounded-lg border border-rose-200 bg-white px-2.5 py-1 text-xs font-semibold text-rose-700 shadow-sm hover:bg-rose-100">
                  <RotateCcw className="h-3.5 w-3.5" /> Réessayer
                </button>
              )}
            </div>
          </div>
        ) : content ? (
          <div className={streaming ? 'nexus-streaming' : ''}>
            {renderContent(content)}
          </div>
        ) : null}
        {content && !streaming && !live && !isError && (
          <div className="mt-1 h-5 opacity-0 transition group-hover:opacity-100 focus-within:opacity-100">
            <CopyButton text={content} />
          </div>
        )}
      </div>
    </div>
  );
};

ChatBubble.propTypes = {
  onRetry: PropTypes.func,
  message: PropTypes.shape({
    content: PropTypes.string,
    role: PropTypes.oneOf(['user', 'assistant', 'system']).isRequired,
    agent: PropTypes.string,
    isError: PropTypes.bool,
    streaming: PropTypes.bool,
    run: PropTypes.object,
  }).isRequired,
  live: PropTypes.bool,
};

export default ChatBubble;
